import images from '@/utils/images';

export interface ITraySolution {
  description: string;
  id: string;
  tags: string[];
  title: string;
  type?: EMappedIntegrationTypes;
}

export interface ITraySolution {
  description: string;
  id: string;
  tags: string[];
  title: string;
  type?: EMappedIntegrationTypes;
}

// The tag values will be used to filter
export const SolutionTagTypes = {
  // Environment tag types
  Environment: {
    development: 'stage_development',
    production: 'stage_production',
    // add more types as they are added to workflow
  },

  // Instance type tags, these will determine where in the live event flow the data is sent
  // as well as the format of data that is sent
  InstanceType: {
    // registration type sends all registration data gathered from every event owned by a given channel, to th endpoint of the instance
    registration: 'type_registration',

    // inbound defines an instance that is receiving data from am external source, and storing it within the app
    inbound: 'type_inbound',

    // activity defines an integration that can be triggered by multiple different user interactions on the event page
    activity: 'type_activity',
    // add more tag types as we add different integration types
  },

  // Option tags are miscellaneous options that can be added to a solution instance
  // These options allow different interactions than the traditional integrations workflow
  Options: {
    // no auth defines an instance that doesn't require any authentication values to enable
    no_auth: 'no_auth',

    // show passcodes defines an instance that displays a list of passcode lists when configuring the instance
    // more information about this can be found in the Tray Reference doc
    show_passcodes: 'show_passcodes',

    // indicates this is an integration that follows the new custom mapping flow.
    // These integrations are treated differently than the standard integrations in both appearance and behavior.
    custom_mapping: 'mapping',
  },
};

export enum EMappedIntegrationTypes {
  registration = 'registration',
  inbound = 'inbound',
  activity = 'activity',
}

export const EMappedIntegrationTypeIndex: Record<EMappedIntegrationTypes, number> = {
  [EMappedIntegrationTypes.registration]: 0,
  [EMappedIntegrationTypes.inbound]: 1,
  [EMappedIntegrationTypes.activity]: 2,
};

export interface IntegrationsMappedDB {
  instance_uuid: string;
  channel: number;
  solution_uuid: string;
  external_uuid: string;
  name: string;
  webhook_url: string;
  enabled: boolean;
  type: EMappedIntegrationTypes;
  created_by: number;
  created_at: string;
  modified_by?: number;
  modified_at?: string;
  registration_mapping: { [key: string]: string };
  query_mapping: { [key: string]: string };
  outbound_activity_configuration: { crm?: string; enabled_actions: { [key: string]: ActivityConfig } };
  static_mapping?: { [key: string]: string };
  opt_in_mapping: string;
  action_endpoint: string;
  action_label: string;
  action_description?: string;
  action_title_keyname: string;
  confirmation_url_mapping: string | null;
  event_groups: string[];
  all_events: boolean;
  convert_non_ascii_characters?: boolean;
}

export interface ActivityConfig {
  label: string;
  value: string;
  enabled: boolean;
  activityTypeId?: number;
}

export interface IntegratedEvent {
  instance_uuid: string;
  event_uuid: string;
}

export interface IntegratedEventsDB extends IntegratedEvent {
  id: number;
  created_at: string;
  created_by: number;
}

export interface AvailableInstance extends IntegrationsMappedDB {
  integrations_event?: IntegratedEventsDB;
}

export enum ESolutionNames {
  Salesforce = 'Salesforce',
  Hubspot = 'HubSpot',
  AddThis = 'AddThis',
  GoogleAnalytics = 'Google Analytics',
  Saml = 'SAML',
  Marketo = 'Marketo',
  SurveyMonkey = 'SurveyMonkey',
  Custom = 'Custom',
}

export const SolutionImages: Map<string, string | undefined> = new Map([
  [ESolutionNames.Salesforce, images.Salesforce2],
  [ESolutionNames.Hubspot, images.Hubspot1],
  [ESolutionNames.Marketo, 'https://assets.brandlive.com/assets/bl-platform/images/Marketo_alt.png'],
  [ESolutionNames.SurveyMonkey, 'https://assets.brandlive.com/assets/SurveyMonkey_Logo.svg'],
  [ESolutionNames.AddThis, images.AddThis],
  [ESolutionNames.GoogleAnalytics, images.IcAnalytics],
  [ESolutionNames.Saml, images.SAMLLogo],
  [ESolutionNames.Custom, images.TellyLogo],
]);

export interface InstanceMappingDB {
  channel_mapping: IntegrationsMappedDB;
  entity_mapping: IntegrationsMappedDB;
}

export interface IntegrationAction {
  action_endpoint: string;
  action_label: string;
  action_description?: string;
}

export interface IntegrationsMappedOptions {
  id?: number;
  registration_mapping: { [key: string]: string };
  query_mapping: { [key: string]: string };
  outbound_activity_configuration: { crm?: string; enabled_actions: { [key: string]: ActivityConfig } };
  static_mapping?: { [key: string]: string };
  opt_in_mapping: string;
  action: IntegrationAction;
  action_title_keyname?: string;
  confirmation_url_mapping: string | null;
  convert_non_ascii_characters?: boolean;
}

export interface InsertMapOverridesBody extends IntegrationsMappedOptions {
  channel: number;
  entity_uuid: string;
  type: EIntegrationOverrideType;
  instance_uuid: string;
}

export enum EIntegrationOverrideType {
  event = 'event',
}

export enum SetIntegrationsModalPhases {
  init,
  groups,
}

export interface IntegrationAction {
  action_endpoint: string;
  action_label: string;
  action_description?: string;
}

export interface IntegrationActionDB extends IntegrationAction {
  id: number;
  solution_uuid: string;
}
export interface InsertIntegrationsMappedBody extends IntegrationsMappedOptions {
  channel: number;
  solution_uuid: string;
  type: EMappedIntegrationTypes;
  tray_id: string;
  custom_name: string;
  config_values?: InstanceConfigValue[];
  event_groups: string[];
  all_events: boolean;
  confirmation_url_mapping: string | null;
  crm?: string;
  convert_non_ascii_characters?: boolean;
}

export type InsertIntegrationsMappedResponse = {
  inserted: IntegrationsMappedDB;
  integrations_events: IntegratedEventsDB[];
};

export type InsertMapOverridesResponse = {
  inserted: IntegrationsMapOverridesDB;
};

export interface UpdateIntegrationsMappedBody extends IntegrationsMappedOptions {
  channel: number;
  instance_uuid: string;
  enabled: boolean;
  config_values?: InstanceConfigValue[];
  tray_id: string;
  custom_name: string;
  event_groups: string[];
  all_events: boolean;
  confirmation_url_mapping: string | null;
  type: EMappedIntegrationTypes;
  convert_non_ascii_characters?: boolean;
}

export type UpdateIntegrationsMappedResponse = {
  updated: IntegrationsMappedDB;
};

export interface InstanceConfigValue {
  externalId: { [key: string]: string };
  value: { [key: string]: string };
}

export interface UpdateMapOverridesBody extends IntegrationsMappedOptions {
  channel: number;
  entity_uuid: string;
  instance_uuid: string;
}

export interface IntegrationsMapOverridesDB {
  id: number;
  entity_uuid: string;
  type: EIntegrationOverrideType;
  instance_uuid: string;
  action_endpoint: string;
  action_label: string;
  action_description?: string;
  action_title_keyname?: string;
  registration_mapping: { [key: string]: string };
  query_mapping: { [key: string]: string };
  static_mapping: { [key: string]: string };
  outbound_activity_configuration: { crm?: string; enabled_actions: { [key: string]: ActivityConfig } };
  created_at: string;
  created_by: number;
  modified_at: string;
  modified_by: number;
}

export type UpdateMapOverridesResponse = {
  updated: IntegrationsMapOverridesDB;
};

export interface GlobalPrice {
  price: number;
  currency: string;
  multiplier: number;
  // New parameters must be optional or nullable
}

export interface GlobalCurrency {
  code: string;
  name: string;
  stripe_multiplier: number;
  accepted: boolean;
  // New parameters must be optional or nullable
}

export interface ExchangeResponse {
  base: string;
  last_updated: number;
  conversion_rates: { [country_code: string]: number };
  // New parameters must be optional or nullable
}

export interface ITrayUser {
  user: number;
  tray_id: string;
  channel: number;
  name: string;
  integrations: number[] | null;
  status: number;
}
export interface ITraySolutionInstance {
  authValues: {
    authId: string;
    externalId: string;
  }[];
  configValues: {
    externalId: string;
    value: string | null;
  }[];
  created: string;
  enabled: boolean;
  id: string;
  name: string;
  owner: string;
  solutionVersionFlags: {
    hasNewerVersion: boolean;
    requiresSystemInputToUpdateVersion: boolean;
    requiresUserInputToUpdateVersion: boolean;
  };
  solution: {
    id: string;
  };
}

export const SolutionConfigKeys = {
  // NOTE: tray io state that all config values that are accessed through their api must be prefixed with the string "external_"
  // These config keys will need to be verified in the tray workflow
  external_passcode: 'external_passcode',
  external_mapping: 'external_mapping',
};

export enum StripeStatus {
  none = 'none',
  ready = 'ready',
  incomplete = 'incomplete',
}

export interface StripeLinkResponse {
  url: string;
}

export interface StripeResponse {
  stripe_status: StripeStatus;
  primary_contact: string | null;
}

export enum EListTypes {
  emailOnly = 'email-only',
  emailAndPasscode = 'email-passcode',
}
